/**
 * Shared, defensive read helpers for support tickets.
 *
 * ms-agents ticket payloads are not fully finalised and this console must never
 * crash on missing fields, so every accessor falls back gracefully across the
 * plausible field names.
 */

/** First-response SLA window (2 hours) used when the server omits a due time. */
const SLA_FIRST_RESPONSE_MS = 2 * 60 * 60 * 1000;

/** Below this many minutes remaining, the SLA chip turns red/urgent. */
const SLA_URGENT_MIN = 30;

/**
 * Room id backing a ticket (used as the ticket's stable key + Thread roomId).
 * @param {Object} t
 * @returns {string|undefined}
 */
export const getRoomId = (t) => t?.room_id || t?.roomId || t?.id;

/**
 * Human ticket number.
 * @param {Object} t
 * @returns {string}
 */
export const getDisplayId = (t) =>
  t?.display_id || t?.displayId || getRoomId(t) || "";

/**
 * Client name embedded directly on a ticket, if the payload includes one.
 * Callers should fall back to the shared identity cache (`useCommsIdentity`)
 * keyed on `t.client_id` before defaulting to a generic label — this only
 * reads fields ms-agents may already provide inline.
 * @param {Object} t
 * @returns {string|undefined}
 */
export const getTicketClientName = (t) => t?.client_name || t?.client || undefined;

/**
 * Assignee display name (falls back to a generic label when unassigned).
 * @param {Object} t
 * @returns {string}
 */
export const getAssignee = (t) =>
  t?.assignee_name || t?.assignee || t?.agent_name || "";

/**
 * Assignee user id across the plausible field names (null when unassigned).
 * @param {Object} t
 * @returns {string|number|null}
 */
export const getAssigneeId = (t) =>
  // `assigned_agent_id` is the field v1.9 actually ships (guide §13.1). It was
  // missing here while `normalizeTicket` had it, so the queue row showed an
  // owner while the ticket header still offered "Pick up" — the same fact read
  // two ways. This is now the single definition; normalizeTicket calls it.
  t?.assigned_agent_id ??
  t?.assignee_user_id ??
  t?.agent_user_id ??
  t?.assignee_id ??
  t?.assignee?.user_id ??
  null;

/**
 * Workflow state of the ticket, uppercased — `OPEN` | `PENDING` | `RESOLVED` |
 * `CLOSED`. Distinct from `status`, which is the *room* (`active` / `resolved`
 * / `archived`). `null` when the payload predates v1.9.
 * @param {Object} t
 * @returns {string|null}
 */
export const getTicketStatus = (t) => {
  const raw = t?.ticket_status || t?.ticketStatus;
  return typeof raw === "string" ? raw.toUpperCase() : null;
};

/**
 * True when a ticket is no longer open — resolved, or closed by a merge.
 * @param {Object} t
 * @returns {boolean}
 */
export const isResolved = (t) => {
  const workflow = getTicketStatus(t);
  if (workflow === "RESOLVED" || workflow === "CLOSED") return true;
  return t?.status === "resolved" || Boolean(t?.resolved_at);
};

/**
 * True when a ticket has been escalated to a second agent.
 * @param {Object} t
 * @returns {boolean}
 */
const isEscalated = (t) =>
  t?.status === "escalated" ||
  Boolean(t?.escalated) ||
  (Array.isArray(t?.escalations) && t.escalations.length > 0);

/**
 * CSAT score attached on resolution.
 * @param {Object} t
 * @returns {number|null}
 */
export const getCsat = (t) => {
  // `GET /support/tickets/{room_id}` returns a whole `csat` *record*
  // (`{rating, comment, …}`), while queue rows carry the flat `csat_rating`.
  // Returning the record would put an object where a number is rendered — an
  // "Objects are not valid as a React child" crash — so only ever hand back a
  // number.
  const raw =
    t?.csat_rating ?? t?.csat_score ?? (typeof t?.csat === "object" ? t?.csat?.rating : t?.csat);
  if (raw == null) return null;
  const rating = Number(raw);
  return Number.isFinite(rating) ? rating : null;
};

/**
 * The full CSAT the customer left: the score, what they wrote, and when.
 *
 * The comment matters as much as the number — "sorted it, but took all day" is
 * a 4 that is really a complaint about the first-reply clock.
 *
 * @param {Object} t
 * @returns {{ rating: number, comment: string|null, at: string|null }|null}
 */
export const getCsatFeedback = (t) => {
  const rating = getCsat(t);
  if (rating == null) return null;
  const record = typeof t?.csat === "object" ? t.csat : null;
  return {
    rating,
    comment: t?.csat_comment || record?.comment || null,
    at: t?.csat_at || record?.created_at || record?.submitted_at || t?.resolved_at || null,
  };
};

/**
 * Ticket priority (`urgent` | `high` | `normal` | `low`), lowercased.
 * Defaults to `normal` — a ticket without one sorts and renders as ordinary.
 * @param {Object} t
 * @returns {string}
 */
export const getPriority = (t) => String(t?.priority || "normal").toLowerCase();

/**
 * Tag names on a ticket. The API returns them derived as an array of strings,
 * but tolerate `{name}` objects so a richer payload still renders.
 * @param {Object} t
 * @returns {string[]}
 */
export const getTags = (t) => {
  const raw = t?.tags;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((tag) => (typeof tag === "string" ? tag : tag?.name))
    .filter((name) => typeof name === "string" && name.length > 0);
};

/**
 * Which side the ball is in — `agent` while the next-response clock runs,
 * `customer` while it is paused. `null` when the server doesn't say.
 * @param {Object} t
 * @returns {'agent'|'customer'|null}
 */
const getWaitingOn = (t) => {
  const raw = t?.waiting_on || t?.waitingOn;
  return raw === "agent" || raw === "customer" ? raw : null;
};

/**
 * True while a ticket is snoozed out of the open queue. A past `snoozed_until`
 * means it has already woken.
 * @param {Object} t
 * @param {number} [now]
 * @returns {boolean}
 */
export const isSnoozed = (t, now = Date.now()) => {
  const until = t?.snoozed_until || t?.snoozedUntil;
  if (!until) return false;
  const ms = Date.parse(until);
  return !Number.isNaN(ms) && ms > now;
};

/**
 * Room id this ticket was merged into, or `null`. Merge is link-only: the
 * loser keeps its messages and points here.
 * @param {Object} t
 * @returns {string|null}
 */
export const getMergedIntoRoomId = (t) =>
  t?.merged_into_room_id || t?.mergedIntoRoomId || null;

/**
 * True when this ticket has been merged away into another.
 * @param {Object} t
 * @returns {boolean}
 */
export const isMerged = (t) => Boolean(getMergedIntoRoomId(t));

/**
 * Compact "age" of a ticket from its creation time.
 * @param {string} iso
 * @returns {string}
 */
export const formatAge = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  return `${day}d`;
};

/** Timestamp of the first customer-visible agent reply, if any. */
const firstRespondedAt = (t) =>
  t?.first_responded_at || t?.first_response_at || t?.responded_at || null;

/** `h:mm` countdown from a millisecond remainder. */
const formatCountdown = (ms) => {
  const totalMin = Math.floor(ms / 60000);
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  return `${hours}:${String(mins).padStart(2, "0")}`;
};

/**
 * Derive the SLA chip state for a ticket.
 *
 * v1.9 runs two clocks (see the integration guide §13.8):
 *  - **first response** — starts at creation, stops at the first
 *    customer-visible agent message. Neither an internal note nor picking the
 *    ticket up stops it.
 *  - **next response** — restarts on every customer message and pauses while
 *    `waiting_on === "customer"`.
 *
 * The server's `sla_breached` is authoritative when present; the per-clock
 * `*_breached_at` fields say which one blew. Everything falls back to the
 * pre-v1.9 single-clock behaviour when the new fields are absent.
 *
 * The clock runs 24/7 — an overnight ticket breaches. SLA policies carry
 * `timezone`/`business_hours` but v1.9 does not apply them.
 *
 * @param {Object} t
 * @param {number} [now] - Injectable clock for tests.
 * @returns {{ kind: 'escalated'|'paused'|'urgent'|'breach'|'in', label: string,
 *   clock: 'first'|'next'|null }}
 */
export const computeSla = (t, now = Date.now()) => {
  if (isEscalated(t)) return { kind: "escalated", label: "Escalated", clock: null };

  const firstBreachedAt = t?.first_response_breached_at || null;
  const nextBreachedAt = t?.next_response_breached_at || null;
  if (firstBreachedAt || nextBreachedAt) {
    // Whichever clock latched is the one to name; a first-response breach is
    // the more damning of the two, so it wins when both did.
    const clock = firstBreachedAt ? "first" : "next";
    return {
      kind: "breach",
      label: clock === "first" ? "First reply breached" : "Reply breached",
      clock,
    };
  }
  if (t?.sla_breached === true) {
    return { kind: "breach", label: "SLA breached", clock: null };
  }

  // Paused: the customer owes us the next move, so no clock is running.
  if (getWaitingOn(t) === "customer") {
    return { kind: "paused", label: "Waiting on customer", clock: null };
  }

  const responded = firstRespondedAt(t);
  const clock = responded ? "next" : "first";
  const dueIso = responded
    ? t?.next_response_due_at || t?.nextResponseDueAt || null
    : t?.sla_first_response_due_at ||
      t?.first_response_due_at ||
      t?.sla_due_at ||
      (t?.created_at
        ? new Date(Date.parse(t.created_at) + SLA_FIRST_RESPONSE_MS).toISOString()
        : null);

  // No deadline to measure against — a replied-to ticket with no next-response
  // clock is simply in good standing.
  if (!dueIso) return { kind: "in", label: "In SLA", clock: null };

  const msLeft = Date.parse(dueIso) - now;
  if (Number.isNaN(msLeft)) return { kind: "in", label: "In SLA", clock: null };
  if (msLeft <= 0) {
    return {
      kind: "breach",
      label: clock === "first" ? "First reply breached" : "Reply breached",
      clock,
    };
  }

  if (msLeft / 60000 < SLA_URGENT_MIN) {
    return { kind: "urgent", label: `${formatCountdown(msLeft)} left`, clock };
  }
  return { kind: "in", label: "In SLA", clock };
};

/**
 * Extract a ticket array from the various list payload shapes ms-agents may use.
 * @param {*} data
 * @returns {Array<Object>}
 */
export const extractTickets = (data) => {
  if (Array.isArray(data)) return data;
  return data?.tickets || data?.items || data?.data || [];
};

/**
 * Unwrap a single-ticket payload (getTicket / assignTicket responses) to the
 * ticket core across the plausible shapes — `{ticket: {...}, client_context}`,
 * `{data: {...}}`, or already-flat. No-op for flat payloads.
 * @param {*} d
 * @returns {Object|null}
 */
export const extractTicketDetail = (d) =>
  d?.ticket || d?.data?.ticket || d?.data || d || null;
